import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { API } from 'aws-amplify'
import { Logger } from '@meprism/app-utils'
import { Toast } from '../components/toast/toast'
import { RootState } from './store'
import { useSelector } from 'react-redux'
import { lastSyncInitialState, AllCorporateStatsInitialState, AllFamilyStatsInitialState, AllIndividualStatsType
  , AllIndividualStatsInitialState,
  RemovalStatsType
} from '../components/Views/UserReports/Types'

export type SearchSuggestionsRequest = {
  key: string
  value: string
  page_size: number
  offset: number 
}

export type RangeFilters = {
   key: string
   first_value: string
   second_value: string 
}

export type ExportEmailRequest = {
  search_key: string
  search_value: string
  emails: string | string[]
  cc_emails: string | string[]
  bcc_emails: string | string[]
}

export type FiltersType = {
  filters: object
  date_range_filter: RangeFilters[]
  search_filter: object
  number_range_filter?: RangeFilters[]
  broker_source_filter?: string
}

export type PaginatedReportArgs = {
    table : string, 
    page_size: number,
    offset: number,
    sort_by: string, 
    order: "ASC" | "DESC",
    Filters: FiltersType
}

export type PaginatedReportReturn={
  data: any[],
  has_next: boolean
}

export type SearchSuggestionsReturn={
  result: string[],
  has_next: boolean
}

export type AccountSubscriptionReportReturn = {
    month_year: string,
    user_count: string,
    subscription_count: string
}[]

export type SubscriptionReportArgs = {
    start_time_str: string
    end_time_str: string
    active: string
}

export type SubscriptionReportReturn={
  subscription: string,
  count: string
}[]

export type CorporateCodePostData = {
  active: boolean
  allocated: number
  code: string
  create_time: string
  creator_arn: string
  description: string
  duration: number
  duration_type: string
  end_date: string | null
  entitlements: string[]
  expiration_date: string
  limit: number
  owner_buid: string
  title: string
  update_time: string
  _id: string
  cost_per_employee_cents: number
  authentication_type: 'default' | 'federation'
  federated_type: 'okta' | null
  account_type?: 'standard' | 'executive' | null
  manual_invoice?: boolean
}
export type CorporateCodePost = {
  active: boolean
  code: string
  description: string
  entitlements: string[]
  owner_buid: string
  title: string
  cost_per_employee_cents: number
  type: 'default' | 'federation'
  federation_type: string | null
  authentication?: {
    type: 'default' | 'federation'
    federation_opts: { type: string; application_name: string | null }
  }
  account_type?: string | null
  is_login_enabled?: boolean | null
}

export type CorporateCodePut = {
  corp_code: string
  formData: CorporateCodePost
}

export interface CorporateCodeState {
  codes: CorporateCodePostData[]
  loading: boolean
}

export const initialCorporateCodeState: CorporateCodeState = {
  codes: [] as CorporateCodePostData[],
  loading: false,
}

const createCorporateCodeApi = async (
  data: CorporateCodePost,
): Promise<CorporateCodePost> => {
  return API.post('CorpOnboarding', '/code', { body: data })
}

const getSearchSuggestions= (params: SearchSuggestionsRequest): Promise<SearchSuggestionsReturn>=>{
  return API.post('CorpOnboarding', '/looker-user-search-suggestions', {body: params})
}

export const createSearchSuggestionsApi = createAsyncThunk(
  'createSearchSuggestionsApi',
  async (data: SearchSuggestionsRequest) => {
    try {
      const response : SearchSuggestionsReturn = await getSearchSuggestions(data)
      return response
    } catch (error) {
      Logger.error(`Error getting searh suggestions for ${data.key}: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting search suggestions. Try Again.',
      })
      return {
        result: [],
        has_next: false
      }
    }
  },
)

const getPaginatedReport= (params: PaginatedReportArgs): Promise<PaginatedReportReturn>=>{
  return API.post('CorpOnboarding', '/looker-paginated-report', {body: params})
}

export const createPaginatedReportApi = createAsyncThunk(
  'createPaginatedRepoortApi',
  async (data: PaginatedReportArgs) => {
    try {
      const response : PaginatedReportReturn = await getPaginatedReport(data)
      return response
    } catch (error) {
      Logger.error(`Error getting paginated report for ${data.table}: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting report. Try Again.',
      })
      return {
        data: [],
        has_next: false
      }
    }
  },
)

const getLookerCorpCodes = (): Promise<[]>=>{
  return API.get('CorpOnboarding', '/looker-corp-codes', {})
}

export const LookerCorpCodesApi = createAsyncThunk(
  'LookerCorpCodesApi',
  async () => {
    try {
      const response : string[]= await getLookerCorpCodes()
      return response
    } catch (error) {
      Logger.error(`Error getting codes for corporate report: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting codes for corporate report. Try Again.',
      })
      return []
    }
  },
)


const getSubscriptionReport = async (params: SubscriptionReportArgs): Promise<SubscriptionReportReturn>=>{
  return API.post('CorpOnboarding', '/looker-users-sub-stats', {body: params})
}

export const createSubscriptionReportApi = createAsyncThunk(
  'createSubscriptionReportApi',
  async (data: SubscriptionReportArgs) => {
    try {
      const response : SubscriptionReportReturn = await getSubscriptionReport(data)
      return response
    } catch (error) {
      Logger.error(`Error getting subscription report: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting subscription report. Try Again.',
      })
      return []
    }
  },
)

const exportLookerPDF = async (request: ExportEmailRequest): Promise<any>=>{
  return API.post('CorpOnboarding', '/looker-report-pdf', {body: request})
}

export const createExportLookerReportApi = createAsyncThunk(
  'createExportLookerReportApi',
  async (request: ExportEmailRequest) => {
    try {
      await exportLookerPDF(request)
      Toast.show({
        type: 'success',
        text1: 'You will recieve an email with report link shortly'
      })

    } catch (error) {
      const message = error.response?.data?.detail || 'Error exporting report. Please Try Again'
      Logger.error(`Error exporting report: ${message}`)
      Toast.show({
        type: 'error',
        text1: `${message}`,
      })
    }
  },
)

const getAccountSubscriptionReport = async (year: string): Promise<AccountSubscriptionReportReturn>=>{
  return API.post('CorpOnboarding', '/looker-yearly-users-stats', {body: {'year': year}})
}

export const createAccountSubscriptionReportApi = createAsyncThunk(
  'createAccountSubscriptionReportApi',
  async (year: string) => {
    try {
      const response : AccountSubscriptionReportReturn = await getAccountSubscriptionReport(year)
      return response
    } catch (error) {
      Logger.error(`Error getting accounts and subscription report: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting accounts and report. Try Again.',
      })
      return []
    }
  },
)

const getLookerReportsLastSync= (): Promise<any[]>=>{
  return API.get('CorpOnboarding', '/looker-reports-sync-time', {})
}

export const createLookerReportLastSyncApi = createAsyncThunk(
  'createLookerReportLastSyncApi',
  async () => {
    try {
      const response = await getLookerReportsLastSync()
      return response
    } catch (error) {
      Logger.error(`Error getting looker reports last sync: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting reports last sync time.',
      })
      return lastSyncInitialState
    }
  },
)

const getLookerAllCorporateStats= (): Promise<Record<string, number | RemovalStatsType>>=>{
  return API.get('CorpOnboarding', '/looker-all-corporate-stats', {})
}

export const createLookerAllCorporateStatsApi = createAsyncThunk(
  'createLookerAllCorporateStatsApi',
  async () => {
    try {
      const response = await getLookerAllCorporateStats()
      return response
    } catch (error) {
      Logger.error(`Error getting looker all corporate stats: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting all corporate stats',
      })
      return AllCorporateStatsInitialState
    }
  },
)

const getLookerAllFamilyStats= (): Promise<Record<string,number>>=>{
  return API.get('CorpOnboarding', '/looker-all-family-stats', {})
}

export const createLookerAllFamilyStatsApi = createAsyncThunk(
  'createLookerAllFamilyStatsApi',
  async () => {
    try {
      const response = await getLookerAllFamilyStats()
      return response
    } catch (error) {
      Logger.error(`Error getting looker all family stats: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting all family stats',
      })
      return AllFamilyStatsInitialState
    }
  },
)

const getLookerAllIndividualStats = (payload: FiltersType): Promise<AllIndividualStatsType>=>{
  return API.post('CorpOnboarding', '/looker-all-individual-user-stats', {body: payload})
}

export const createLookerAllIndividualUserStatsApi = createAsyncThunk(
  'createLookerAllIndividualUserStatsApi',
  async (payload: FiltersType) => {
    try {
      const response = await getLookerAllIndividualStats(payload)
      return response
    } catch (error) {
      Logger.error(`Error getting looker all individual user stats: ${error}`)
      Toast.show({
        type: 'error',
        text1: 'Error getting all individual user stats',
      })
      return AllIndividualStatsInitialState
    }
  },
)

const updateCorporateCodeApi = (
  corp_code: string,
  data: CorporateCodePost,
): Promise<CorporateCodePost> => {
  return API.put('CorpOnboarding', `/code/${corp_code}`, { body: data })
}

const getCorporateCodeApi = async (): Promise<any> => {
  return API.get('CorpOnboarding', '/codes', {})
}

const deactivateCorporateCodeApi = async (
  corp_code: string,
  deleteFlag: boolean,
): Promise<{ status: string; message: string }> => {
  return API.post('CorpOnboarding', `/code/${corp_code}/deactivate`, {body: { delete_data: deleteFlag }})
}

export const createCorporateCode = createAsyncThunk(
  'createCorporateCode',
  async (data: CorporateCodePost) => {
    try {
      const response = await createCorporateCodeApi(data)
      Toast.show({ type: 'success', text1: 'Corporate code has been created' })
      return response
    } catch (error) {
      Logger.error(`Error creating corporate code: ${error}`)
      const errorMessage =
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : 'Corporate code cannot be created'
      Toast.show({
        type: 'error',
        text1: errorMessage,
      })
      throw error
    }
  },
)

export const updateCorporateCode = createAsyncThunk(
  'updateCorporateCode',
  async (allData: CorporateCodePut) => {
    try {
      const { corp_code, formData } = allData
      const response = await updateCorporateCodeApi(corp_code, formData)
      Toast.show({ type: 'success', text1: 'Corporate code has been updated' })
      return response
    } catch (error) {
      Logger.error(`Error updating corporate code: ${error}`)
      const errorMessage =
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : 'Corporate code cannot be created'
      Toast.show({
        type: 'error',
        text1: errorMessage,
      })
      throw error
    }
  },
)

export const deactivateCorporateCode = createAsyncThunk(
  'deactivateCorporateCode',
  async ({ corp_code, deleteFlag }: { corp_code: string; deleteFlag: boolean }) => {
    try {
      const response = await deactivateCorporateCodeApi(corp_code, deleteFlag)
      Toast.show({ type: 'success', text1: 'Corporate code has been deactivated. User deletion has been scheduled' })
      return response
    } catch (error) {
      Logger.error(`Error deactivating corporate code: ${error}`)
      const errorMessage =
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : 'Corporate code cannot be deactivated'
      Toast.show({
        type: 'error',
        text1: errorMessage,
      })
      throw error
    }
  },
)

export const fetchAllCodes = createAsyncThunk('fetchAllCodes', async () => {
  try {
    const response = await getCorporateCodeApi()
    return response
  } catch (error) {
    Logger.error(`Error fetching users: ${error}`)
    Toast.show({
      type: 'error',
      text1: 'Error fetching corporate codes',
    })
    throw error
  }
})

const { actions, reducer } = createSlice({
  name: 'corporateCodeService',
  initialState: initialCorporateCodeState,
  reducers: {
    populateCodes: (
      state,
      { payload }: { payload: CorporateCodePostData[] },
    ) => {
      state.codes = payload
    },
  },
  extraReducers: (builder) => {
    // Add extra reducers if needed
    builder.addCase(fetchAllCodes.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchAllCodes.fulfilled, (state, { payload }) => {
      state.codes = payload.result
      state.loading = false
    })
    builder.addCase(fetchAllCodes.rejected, (state) => {
      state.loading = false
    })
  },
})

export const CorporateCodeServiceActions = actions
export const CorporateCodeServiceReducer = reducer

export const CorporateCodeServiceSelector = () => (state: RootState) =>
  state.corporateCodeService

export const CorporateCodesSelector = () => {
  return useSelector((state: RootState) => state.corporateCodeService.codes)
}

export const CorporateCodeLoadingSelector = () => {
  return useSelector((state: RootState) => state.corporateCodeService.loading)
}

// Admin Remove User APIs
export type RemoveUserRequest = {
  target_muid: string
  otp_code: string
  target_email?: string
}

const removeUserApi = (data: RemoveUserRequest): Promise<{ message: string }> => {
  return API.post('CorpOnboarding', '/remove-user', { body: data })
}

export const removeUser = createAsyncThunk(
  'removeUser',
  async (data: RemoveUserRequest) => {
    try {
      const response = await removeUserApi(data)
      Toast.show({
        type: 'success',
        text1: 'User has been removed successfully',
      })
      return response
    } catch (error) {
      Logger.error(`Error removing user: ${error}`)
      const errorMessage =
        error.response?.data?.detail || 'Error removing user. Please try again.'
      Toast.show({
        type: 'error',
        text1: errorMessage,
      })
      throw error
    }
  }
)

// OTP Event API
const sendOtpEventApi = (): Promise<{ message: string }> => {
  return API.post('CorpOnboarding', '/remove-user-otp', {})
}

export const sendOtpEvent = createAsyncThunk(
  'sendOtpEvent',
  async () => {
    try {
      const response = await sendOtpEventApi()
      Toast.show({
        type: 'success',
        text1: 'OTP sent to your email',
      })
      return response
    } catch (error) {
      Logger.error(`Error sending OTP: ${error}`)
      const errorMessage =
        error.response?.data?.detail || 'Error sending OTP. Please try again.'
      Toast.show({
        type: 'error',
        text1: errorMessage,
      })
      throw error
    }
  }
)